import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader as Loader2, Mail, Lock, User, CheckCircle, XCircle } from 'lucide-react';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

interface WorkspaceInvitation {
  id: string;
  workspace_id: string;
  email: string;
  role: 'admin' | 'member';
  status: string;
  expires_at: string;
  workspace_name?: string;
}

export const AcceptInvitation: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const [invitation, setInvitation] = useState<WorkspaceInvitation | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Signup form state (for new users)
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [signingUp, setSigningUp] = useState(false);

  const token = searchParams.get('token');
  const workspaceId = searchParams.get('workspace');

  useEffect(() => {
    if (authLoading) return; // Wait for auth session to be verified or cleared

    if (token && workspaceId) {
      loadInvitation();
    } else {
      setError('Invalid invitation link. Missing token or workspace ID.');
      setLoading(false);
    }
  }, [token, workspaceId, authLoading]);

  useEffect(() => {
    if (invitation?.email) {
      setEmail(invitation.email);
    }
  }, [invitation]);

  const loadInvitation = async () => {
    if (!token || !workspaceId) return;

    try {
      const { data, error: fetchError } = await supabase
        .from('workspace_invitations')
        .select('*')
        .eq('token', token)
        .eq('workspace_id', workspaceId)
        .eq('status', 'pending')
        .maybeSingle();

      if (fetchError) throw fetchError;

      if (!data) {
        setError('Invitation not found or has already been accepted.');
        setLoading(false);
        return;
      }

      // Check if invitation is expired
      if (new Date(data.expires_at) < new Date()) {
        setError('This invitation has expired. Please request a new invitation.');
        setLoading(false);
        return;
      }

      setInvitation(data);
      setEmail(data.email);
    } catch (err: unknown) {
      console.error('Error loading invitation:', err);
      setError('Failed to load invitation. Please check your link and try again.');
    } finally {
      setLoading(false);
    }
  };

  const callAcceptFunction = async (action: 'accept' | 'signup') => {
    if (!token) return;
    
    const payload = {
      action,
      token,
      userId: user?.id,
      password: password || undefined,
      firstName: firstName || undefined,
      lastName: lastName || undefined
    };

    const { data, error } = await supabase.functions.invoke('accept-invitation', {
      body: payload
    });

    if (error) {
       console.error("Function Error Payload", error);
       throw new Error(error.message || 'Error executing invitation acceptance');
    }
    
    return data;
  };

  const handleAcceptInvitation = async () => {
    if (!user || !invitation || !token) {
      setError('You must be logged in to accept this invitation.');
      return;
    }

    setAccepting(true);
    setError(null);

    try {
      // Verify user email matches invitation
      if (user.email?.toLowerCase() !== invitation.email.toLowerCase()) {
        setError('This invitation was sent to a different email address. Please log in with the correct account.');
        setAccepting(false);
        return;
      }

      const response = await callAcceptFunction('accept');
      
      if (response && response.error) {
         throw new Error(response.error);
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/home?modal=settings&tab=workspace');
      }, 2000);
    } catch (err: unknown) {
      console.error('Error accepting invitation:', err);
      // Supabase edge functions often return error inside data in 400 responses
      const checkErr = err as any;
      const errorMessage = err instanceof Error ? err.message : (checkErr?.context?.error || 'Failed to accept invitation. Please try again.');
      setError(errorMessage);
    } finally {
      setAccepting(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setSigningUp(true);
    setError(null);

    // Basic Validation
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setSigningUp(false);
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setSigningUp(false);
      return;
    }

    if (email.toLowerCase() !== invitation?.email.toLowerCase()) {
      setError('Email must match the invitation email');
      setSigningUp(false);
      return;
    }

    try {
       const response = await callAcceptFunction('signup');
       
       if (response && response.error) {
          throw new Error(response.error);
       }
       
       // Because standard signup doesn't log them in automatically inside the Edge function context,
       // we automatically sign them in locally using the credentials they just successfully registered with.
       const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
       
       if (signInError) {
           console.warn('Sign in after signup failed:', signInError.message);
       }
       
       setSuccess(true);
       setTimeout(() => {
         navigate('/home?modal=settings&tab=workspace');
       }, 2000);
       
    } catch (err: unknown) {
      console.error('Error signing up:', err);
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(errorMessage || 'Failed to create account. Please try again.');
    } finally {
      setSigningUp(false);
    }
  };

  if (authLoading || loading) {
    return (
        null
    );
  }

  if (error && !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
        <Card className="border-slate-700 bg-slate-800/50 backdrop-blur-sm shadow-xl max-w-md w-full">
          <CardHeader>
            <div className="flex items-center justify-center w-16 h-16 bg-red-600 rounded-full mx-auto mb-4">
              <XCircle className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-white text-center">Invalid Invitation</CardTitle>
            <CardDescription className="text-slate-400 text-center">
              {error}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Link to="/login">
              <Button variant="outline" className="w-full">
                Go to Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }


  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
        <Card className="border-slate-700 bg-slate-800/50 backdrop-blur-sm shadow-xl max-w-md w-full">
          <CardHeader>
            <div className="flex items-center justify-center w-16 h-16 bg-green-600 rounded-full mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-white text-center">Invitation Accepted!</CardTitle>
            <CardDescription className="text-slate-400 text-center">
              You've successfully joined the workspace. Redirecting...
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (!user && invitation) {
    // Show signup form for new users
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-4">
              <img src="/icon-white.svg" alt="AgencyPlus" className="h-16 w-auto object-contain max-h-16" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Accept Invitation</h1>
            <p className="text-slate-400">
              Create your account to join the workspace
            </p>
          </div>

          <Card className="border-slate-700 bg-slate-800/50 backdrop-blur-sm shadow-xl">
            <CardHeader>
              <CardTitle className="text-white">Sign Up</CardTitle>
              <CardDescription className="text-slate-400">
                You've been invited to join a workspace. Create your account to continue.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSignup} className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName" className="text-slate-200">First Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                      <Input
                        id="firstName"
                        type="text"
                        placeholder="John"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        disabled={signingUp}
                        className="pl-10 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="lastName" className="text-slate-200">Last Name</Label>
                    <Input
                      id="lastName"
                      type="text"
                      placeholder="Doe"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      disabled={signingUp}
                      className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-200">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      disabled
                      className="pl-10 bg-slate-700/50 border-slate-600 text-slate-400 cursor-not-allowed"
                    />
                  </div>
                  <p className="text-xs text-slate-500">This email is from your invitation</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-200">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="At least 6 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={signingUp}
                      className="pl-10 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-slate-200">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="Confirm your password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      disabled={signingUp}
                      className="pl-10 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={signingUp}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {signingUp ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    'Create Account & Accept Invitation'
                  )}
                </Button>
              </form>
            </CardContent>
            <CardFooter className="flex justify-center">
              <p className="text-sm text-slate-400">
                Already have an account?{' '}
                <Link
                  to={`/login?redirect=${encodeURIComponent(`/accept-invitation?token=${token}&workspace=${workspaceId}`)}`}
                  className="text-workspace-accent hover:text-workspace-accent-hover transition-colors font-medium"
                >
                  Sign in
                </Link>
              </p>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  // User is logged in - show accept button
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
      <Card className="border-slate-700 bg-slate-800/50 backdrop-blur-sm shadow-xl max-w-md w-full">
        <CardHeader>
          <div className="flex items-center justify-center mb-4">
            <img src="/icon-white.svg" alt="AgencyPlus" className="h-16 w-auto object-contain max-h-16" />
          </div>
          <CardTitle className="text-white text-center">Workspace Invitation</CardTitle>
          <CardDescription className="text-slate-400 text-center">
            You've been invited to join a workspace
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <p className="text-slate-300 text-sm">
              <strong>Email:</strong> {invitation?.email}
            </p>
            <p className="text-slate-300 text-sm">
              <strong>Role:</strong> {invitation?.role ? invitation.role.charAt(0).toUpperCase() + invitation.role.slice(1) : ''}
            </p>
            {invitation?.expires_at && (
              <p className="text-slate-300 text-sm">
                <strong>Expires:</strong> {new Date(invitation.expires_at).toLocaleDateString()}
              </p>
            )}
          </div>

          <Button
            onClick={handleAcceptInvitation}
            disabled={accepting}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
          >
            {accepting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Accepting...
              </>
            ) : (
              'Accept Invitation'
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
